import React, { useEffect, useState } from 'react';
import { AlertCircleIcon, CheckIcon } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { TextField } from '../../components/ui/Field';
import { SecuritySection } from '../../components/admin/SecuritySection';
import { useAdminData } from '../../contexts/AdminDataContext';
type Feedback = 'idle' | 'saved' | 'error';
export function Settings() {
  const {
    settings,
    saveSettings
  } = useAdminData();
  const [draft, setDraft] = useState(settings);
  const [feedback, setFeedback] = useState<Feedback>('idle');
  useEffect(() => {
    setDraft(settings);
  }, [settings]);
  const dirty = JSON.stringify(draft) !== JSON.stringify(settings);
  function field(key: keyof typeof draft) {
    return {
      value: draft[key],
      onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
        setDraft({
          ...draft,
          [key]: event.target.value
        });
        setFeedback('idle');
      }
    };
  }
  return <div className="px-5 py-7 md:px-8 md:py-10" data-id="element-598" data-mp="191v8hyhq1lhh">
      <p className="text-sm text-ink-muted" data-id="element-599" data-mp="2isj3xaoxq05r">Firmaoplysninger, kontakt og standardpriser</p>
      <h1 className="mt-1 font-display text-[clamp(1.8rem,5vw,2.5rem)] font-bold leading-[1.05] tracking-tight text-ink" data-id="element-600" data-mp="2es7hbsd0hprn">
        Indstillinger
      </h1>
      <p className="mt-3 max-w-2xl text-[15px] text-ink-soft" data-id="element-601" data-mp="2spdy5he6dee8">
        Kontaktoplysningerne herunder bruges automatisk overalt på hjemmesiden — i topbjælken, hero-sektionen,
        kontaktafsnittet og footeren. Du skal kun rette dem ét sted.
      </p>

      <form className="mt-7 max-w-2xl space-y-5" onSubmit={event => {
      event.preventDefault();
      const ok = saveSettings(draft);
      setFeedback(ok ? 'saved' : 'error');
    }} data-id="element-602" data-mp="2xrd14q6069an">
        <section className="rounded-card border border-line bg-white p-6" data-id="element-603" data-mp="36jf1kog2npkx">
          <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-604" data-mp="3lh7ed8u8hzvf">Virksomhed</h2>
          <div className="mt-5 grid gap-5 sm:grid-cols-2" data-id="element-605" data-mp="3gi49ppi61vnz">
            <TextField id="in-navn" label="Firmanavn" {...field('companyName')} data-id="element-606" data-mp="3nk9h29sb7pem" />
            <TextField id="in-cvr" label="CVR-nummer" inputMode="numeric" {...field('cvr')} data-id="element-607" data-mp="3ni6i3096ilxr" />
            <TextField id="in-tagline" label="Tagline" className="sm:col-span-2" {...field('tagline')} data-id="element-608" data-mp="3sh9mqjl8yq57" />
          </div>
        </section>

        <section className="rounded-card border border-line bg-white p-6" data-id="element-609" data-mp="65ul48nszf10">
          <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-610" data-mp="b2b6iv3mbfae">Kontaktoplysninger</h2>
          <div className="mt-5 grid gap-5 sm:grid-cols-2" data-id="element-611" data-mp="f2v768neh11a">
            <TextField id="in-tlf" label="Telefonnummer" type="tel" inputMode="tel" hint="Vises i topbjælken, hero, kontakt og footer." {...field('phone')} data-id="element-612" data-mp="k39efoh6g6y9" />
            <TextField id="in-mail" label="E-mail" type="email" {...field('email')} data-id="element-613" data-mp="2lx0cuhy6fizr" />
            <TextField id="in-adresse" label="Adresse" className="sm:col-span-2" {...field('address')} data-id="element-614" data-mp="2qu8vskr5fr22" />
            <TextField id="in-web" label="Hjemmeside" className="sm:col-span-2" {...field('website')} data-id="element-615" data-mp="2eipx9dpcqwbs" />
          </div>
        </section>

        <section className="rounded-card border border-line bg-white p-6" data-id="element-616" data-mp="29ik4tvb9d5sn">
          <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-617" data-mp="24n4s56ozlfxt">Standardpriser</h2>
          <div className="mt-5 grid gap-5 sm:grid-cols-2" data-id="element-618" data-mp="328tr4tcqnhpl">
            <TextField id="in-timepris" label="Timepris (kr.)" inputMode="numeric" {...field('hourlyRate')} data-id="element-619" data-mp="2x9pji75gps1d" />
            <TextField id="in-udkald" label="Udkaldsgebyr (kr.)" inputMode="numeric" {...field('callOutFee')} data-id="element-620" data-mp="2sacv3hyy0cwo" />
          </div>
          <p className="mt-4 text-sm text-ink-muted" data-id="element-621" data-mp="1gw948knnj4vu">
            Bruges som forslag, når du opretter en ny sag. Du kan altid ændre prisen på den enkelte sag.
          </p>
        </section>

        <div className="flex flex-wrap items-center gap-4" data-id="element-622" data-mp="30qk0dgns9wfm">
          <Button type="submit" size="lg" disabled={!dirty} data-id="element-623" data-mp="30p0k3q24nldu">
            Gem ændringer
          </Button>

          {dirty && feedback === 'idle' && <p className="text-[15px] text-ink-muted" data-id="element-624" data-mp="2qtp4z6mt4c5k">Der er ændringer, der ikke er gemt.</p>}

          {feedback === 'saved' && <p role="status" className="flex items-center gap-2 text-[15px] text-ink" data-id="element-625" data-mp="2vpwc32qxvtp3">
              <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-accent-soft text-accent-deep" data-id="element-626" data-mp="3jicrt6n7f9l8">
                <CheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-627" data-mp="39jf8ci4my693" />
              </span>
              Ændringer gemt
            </p>}

          {feedback === 'error' && <p role="alert" className="flex items-start gap-2 text-[15px] text-danger" data-id="element-628" data-mp="3ekl0adqh1phk">
              <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-629" data-mp="1y2xb4ft7rzle" />
              Ændringerne kunne ikke gemmes. Tjek at browseren tillader lagring, og prøv igen.
            </p>}
        </div>
      </form>

      <SecuritySection data-id="element-630" data-mp="g6vkemv8xyuy" />

      <p className="mt-8 max-w-2xl text-sm text-ink-muted" data-id="element-631" data-mp="g7eujhenniyf">
        Sager, kunder, noter, billeder og indstillinger gemmes i denne browser og er der stadig efter
        genindlæsning, log ud og genstart.
      </p>
    </div>;
}